<script module lang="ts">
  import type { Snippet } from 'svelte';

  export interface LanguagePickerContentProps {
    selectedLanguage: string;
    onClick: () => void;
    children?: Snippet;
  }
</script>

<script lang="ts">
  import { page } from '$app/state';
  import { untrack } from 'svelte';
  import { browser } from '$app/environment';

  import { css } from '@agapi/ui/panda/css';
  import { Button } from '@agapi/ui/button';
  import { Popover, PopoverTrigger } from '@agapi/ui/popover';
  import { Combobox } from '@agapi/ui/combobox';
  import { Loader } from '@agapi/ui/loader';
  import { portal } from '@zag-js/svelte';
  import { Globe } from 'phosphor-svelte';

  import LanguagePickerPopover from './language-picker-popover.svelte';
  import LanguagePickerButton from './language-picker-button.svelte';
  import { languagePickerStyles } from './language-picker.styles';
  import { getLocaleFromUrl, getAvailableMarkets } from '../../utils';

  import { getLocale, locales } from '$lib/parachute/runtime';

  import * as m from '$lib/parachute/messages';

  let { country, language } = $state(getLocaleFromUrl(page.url));
  let comboboxRef: any = $state();
  let hasSetInitialValue = $state(false);

  const classes = languagePickerStyles();
  const { sectionContainer } = languagePickerStyles.raw();

  let isLoading = $state(false);

  const COOKIE_NAME = '__agapi_selected_country';

  /**
   * Set the country cookie
   */
  const setCountryCookie = (country: string): void => {
    if (!browser) return;
    // Set cookie that never expires
    document.cookie = `${COOKIE_NAME}=${country}; path=/; max-age=31536000; SameSite=Lax`;
  };

  // Set combobox value when ref and country are available (only once)
  $effect.pre(() => {
    if (comboboxRef && country && !hasSetInitialValue) {
      untrack(() => {
        comboboxRef.setValue([country]);
        hasSetInitialValue = true;
      });
    }
  });

  const loadingOverlayStyles = css.raw({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(5px)',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    zIndex: 2147483647
  });
</script>

<Popover positioning={{ placement: 'bottom-start' }}>
  <PopoverTrigger>
    <Button as="span" variant="primary" size="xs">
      <Globe>
        <title>{m.iconLanguage()}</title>
      </Globe>
      {m[`${country?.toLowerCase?.()}Country`]?.()} / {m[
        `${language?.toLowerCase?.()}Language`
      ]?.()}
    </Button>
  </PopoverTrigger>

  <LanguagePickerPopover>
    <div class={classes.container}>
      <div class={classes.sectionContainer}>
        <h5>{m.changeLang()}</h5>
        <div class={classes.buttonContainer}>
          {#each locales as lang (lang)}
            <LanguagePickerButton
              css={{ minWidth: 110 }}
              flag={lang}
              selected={lang == getLocale()}
              onclick={() => {
                isLoading = true;

                // Construct the new URL
                const languagePath = locales.includes(lang)
                  ? lang === 'sv'
                    ? ''
                    : lang
                  : '';
                const url = new URL(page.url.origin + '/' + languagePath);

                if (
                  page.url.searchParams.has('country') &&
                  page.url.searchParams.get('country') !== 'SE'
                ) {
                  url.searchParams.set('country', page.url.searchParams.get('country') ?? 'SE');
                }

                // Redirect to the new URL
                location.replace(url.toString());
              }}
            >
              {m[`${lang}Language`]?.()}
            </LanguagePickerButton>
          {/each}
        </div>
      </div>

      <div class={css(sectionContainer, { mt: '1rem' })}>
        <h5 class={css({ mb: '7px' })}>{m.changeMarket()}</h5>
        <Combobox
          bind:this={comboboxRef}
          positionerStyles={css.raw({ zIndex: '999!' })}
          items={getAvailableMarkets().map((countryCode) => ({
            value: countryCode,
            label:
              m[`${countryCode?.toLowerCase?.()}Country`]?.() ??
              `${countryCode.toLowerCase()}Country is missing in translation`
          }))}
          onValueChange={({ value: [countryCode] }) => {
            // Don't redirect if we're just setting the initial value
            if (!hasSetInitialValue) {
              return;
            }

            isLoading = true;

            // Save the selected country in cookie
            setCountryCookie(countryCode.toUpperCase());

            const languagePath = locales.includes(language)
              ? language === 'sv'
                ? ''
                : language
              : '';
            const url = new URL(page.url.origin + '/' + languagePath);

            if (countryCode.toUpperCase() !== 'SE') {
              url.searchParams.set('country', countryCode.toLowerCase());
            } else {
              // For SE (default market), ensure no country parameter is set
              url.searchParams.delete('country');
            }

            // Redirect to the new URL
            location.replace(url.toString());
          }}
          placeholder={m.changeMarket()}
          clearOnOpen={true}
        ></Combobox>
      </div>
    </div>
  </LanguagePickerPopover>
</Popover>

{#if isLoading}
  <div use:portal class={css(loadingOverlayStyles)}>
    <Loader type="spinner" size="2xl" color="primary.500" />
  </div>
{/if}
